import { Box, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { formatCount, formatPeriod, Granularity } from "./reportFormat";

export interface NewReturningPoint {
  period: string;
  newMembers: number;
  returningMembers: number;
}

/**
 * Categorical slots 1 and 2 of the reference palette, validated as an adjacent
 * pair in both modes (CVD ΔE 24.7 light). Returning is the base of every bar,
 * so it takes slot 1.
 */
const SERIES = {
  light: { returning: "#2a78d6", fresh: "#eb6834" },
  dark: { returning: "#3987e5", fresh: "#d95926" },
};

const HEIGHT = 240;
const MARGIN = { top: 12, right: 8, bottom: 28, left: 44 };
const GAP = 2;
const RADIUS = 4;

/** 0, then a round number at or above `max`, so the top gridline reads cleanly. */
function niceMax(max: number): number {
  if (max <= 4) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  const step = [1, 2, 2.5, 5, 10].find((candidate) => candidate * magnitude >= max) ?? 10;
  return step * magnitude;
}

/** A bar segment with its top corners rounded and its base square on the baseline. */
function topRounded(x: number, y: number, width: number, height: number, radius: number): string {
  const r = Math.min(radius, width / 2, height);
  return `M${x},${y + height}V${y + r}Q${x},${y} ${x + r},${y}H${x + width - r}Q${x + width},${y} ${x + width},${y + r}V${y + height}Z`;
}

/** New and returning members per period, stacked into the active total. */
export const NewReturningChart: React.FC<{ points: NewReturningPoint[]; granularity: Granularity }> = ({
  points,
  granularity,
}) => {
  const theme = useTheme();
  const colors = SERIES[theme.palette.mode === "dark" ? "dark" : "light"];
  const holder = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    const element = holder.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(240, entry.contentRect.width)));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const top = niceMax(Math.max(0, ...points.map((point) => point.newMembers + point.returningMembers)));
  const y = (value: number) => MARGIN.top + innerHeight - (value / top) * innerHeight;
  const band = points.length ? innerWidth / points.length : innerWidth;
  const barWidth = Math.max(2, Math.min(36, band * 0.7));
  const labelEvery = Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor(innerWidth / 80))));
  const hoveredPoint = hovered === null ? null : points[hovered];

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2.5, mb: 1, fontSize: 13, color: "text.secondary" }} aria-hidden>
        {[
          { label: "Returning members", color: colors.returning },
          { label: "New members", color: colors.fresh },
        ].map((item) => (
          <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: item.color }} />
            {item.label}
          </Box>
        ))}
      </Box>
      <Box ref={holder} sx={{ position: "relative" }} onMouseLeave={() => setHovered(null)}>
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label="New and returning members per period. The table view lists the same numbers."
          style={{ display: "block" }}
        >
          {[0, top / 2, top].map((tick) => (
            <g key={tick}>
              <line
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke={theme.palette.divider}
                strokeWidth={1}
              />
              <text
                x={MARGIN.left - 6}
                y={y(tick)}
                dy="0.32em"
                textAnchor="end"
                fontSize={11}
                fill={theme.palette.text.secondary}
              >
                {formatCount(tick)}
              </text>
            </g>
          ))}
          {points.map((point, index) => {
            const center = MARGIN.left + band * index + band / 2;
            const x = center - barWidth / 2;
            const base = y(0);
            const returningTop = y(point.returningMembers);
            const totalTop = y(point.returningMembers + point.newMembers);
            const returningHeight = base - returningTop;
            // The upper segment gives up GAP pixels at its bottom so the two read as two.
            const freshHeight = Math.max(0, returningTop - totalTop - (point.returningMembers > 0 ? GAP : 0));
            return (
              <g key={point.period}>
                {hovered === index && (
                  <rect x={MARGIN.left + band * index} y={MARGIN.top} width={band} height={innerHeight} fill={theme.palette.action.hover} />
                )}
                {returningHeight > 0 && (
                  <path
                    d={topRounded(x, returningTop, barWidth, returningHeight, point.newMembers > 0 ? 0 : RADIUS)}
                    fill={colors.returning}
                  />
                )}
                {freshHeight > 0 && <path d={topRounded(x, totalTop, barWidth, freshHeight, RADIUS)} fill={colors.fresh} />}
                {index % labelEvery === 0 && (
                  <text x={center} y={HEIGHT - 8} textAnchor="middle" fontSize={11} fill={theme.palette.text.secondary}>
                    {formatPeriod(point.period, granularity === "WEEK" ? "DAY" : granularity)}
                  </text>
                )}
                {/* The hit target is the whole column, not the bar, so short bars are easy to hover. */}
                <rect
                  x={MARGIN.left + band * index}
                  y={MARGIN.top}
                  width={band}
                  height={innerHeight}
                  fill="transparent"
                  onMouseEnter={() => setHovered(index)}
                />
              </g>
            );
          })}
        </svg>
        {hoveredPoint && hovered !== null && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: Math.min(Math.max(MARGIN.left + band * hovered + band / 2, 90), width - 90),
              transform: "translateX(-50%)",
              pointerEvents: "none",
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              boxShadow: 2,
              px: 1.5,
              py: 1,
              fontSize: 13,
              whiteSpace: "nowrap",
            }}
          >
            <Box sx={{ fontWeight: 600, mb: 0.5 }}>{formatPeriod(hoveredPoint.period, granularity)}</Box>
            {[
              { label: "Active", value: hoveredPoint.newMembers + hoveredPoint.returningMembers },
              { label: "Returning", value: hoveredPoint.returningMembers, color: colors.returning },
              { label: "New", value: hoveredPoint.newMembers, color: colors.fresh },
            ].map((row) => (
              <Box key={row.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: row.color ?? "transparent" }} />
                <Box sx={{ color: "text.secondary", flex: 1 }}>{row.label}</Box>
                <Box sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 500, ml: 2 }}>{formatCount(row.value)}</Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
